// src/utils/react.tsx
import React from "react";
import { isBrowserLike } from "./env.js";
import { neverResolve, runAsynchronously } from "./promises.js";
import { deindent } from "./strings.js";
function componentWrapper(displayName, render) {
  const Component = forwardRefIfNeeded(render);
  Component.displayName = displayName;
  return Component;
}
var react18PromiseCache = /* @__PURE__ */ new WeakMap();
function use(promise) {
  if ("use" in React) {
    return React.use(promise);
  } else {
    if (react18PromiseCache.has(promise)) {
      const result = react18PromiseCache.get(promise);
      if (result.status === "pending") {
        throw promise;
      } else if (result.status === "ok") {
        return result.data;
      } else {
        throw result.error;
      }
    } else {
      react18PromiseCache.set(promise, { "status": "pending", progress: void 0 });
      runAsynchronously(async () => {
        try {
          const res = await promise;
          react18PromiseCache.set(promise, { "status": "ok", data: res });
        } catch (e) {
          react18PromiseCache.set(promise, { "status": "error", error: e });
        }
      });
      throw promise;
    }
  }
}
function forwardRefIfNeeded(render) {
  const version = React.version;
  const major = parseInt(version.split(".")[0]);
  if (major < 19) {
    return React.forwardRef(render);
  } else {
    return (props) => render(props, props.ref);
  }
}
function getNodeText(node) {
  if (["number", "string"].includes(typeof node)) {
    return `${node}`;
  }
  if (!node) {
    return "";
  }
  if (Array.isArray(node)) {
    return node.map(getNodeText).join("");
  }
  if (typeof node === "object" && "props" in node) {
    return getNodeText(node.props.children);
  }
  throw new Error(`Unknown node type: ${typeof node}`);
}
function suspend() {
  use(neverResolve());
  throw new Error("Somehow a Promise that never resolves was resolved?");
}
function mapRef(ref, mapper) {
  let last = null;
  return {
    get current() {
      const input = ref.current;
      if (last === null || input !== last[0]) {
        last = [input, mapper(input)];
      }
      return last[1];
    }
  };
}
function useRefState(initialValue) {
  const [, setState] = React.useState(initialValue);
  const ref = React.useRef(initialValue);
  const setValue = React.useCallback((updater) => {
    const value = typeof updater === "function" ? updater(ref.current) : updater;
    ref.current = value;
    setState(value);
  }, []);
  const res = React.useMemo(() => ({
    get current() {
      return ref.current;
    },
    set: setValue
  }), [setValue]);
  return res;
}
function mapRefState(refState, mapper, reverseMapper) {
  let last = null;
  return {
    get current() {
      const input = refState.current;
      if (last === null || input !== last[0]) {
        last = [input, mapper(input)];
      }
      return last[1];
    },
    set(updater) {
      const value = typeof updater === "function" ? updater(this.current) : updater;
      refState.set(reverseMapper(refState.current, value));
    }
  };
}
function useQueryState(key, defaultValue) {
  const getValue = () => new URLSearchParams(window.location.search).get(key) ?? defaultValue ?? null;
  const [value, setValue] = React.useState(getValue);
  React.useEffect(() => {
    const onPopState = () => setValue(getValue());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);
  const update = (next) => {
    const params = new URLSearchParams(window.location.search);
    if (next !== null) {
      params.set(key, next);
    } else {
      params.delete(key);
    }
    const newUrl = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
    window.history.pushState(null, "", newUrl);
    setValue(next);
  };
  return [value, update];
}
function shouldRethrowRenderingError(error) {
  return !!error && typeof error === "object" && "digest" in error && error.digest === "BAILOUT_TO_CLIENT_SIDE_RENDERING";
}
var NoSuspenseBoundaryError = class extends Error {
  constructor(options) {
    super(deindent`
      Suspense boundary not found! Read the error message below carefully on how to fix it.

      ${options.caller ?? "This code path"} attempted to display a loading indicator, but didn't find a Suspense boundary above it. Please read the error message below carefully.
      
      The fix depends on which of the 4 scenarios caused it:
      
      1. [Next.js] You are missing a loading.tsx file in your app directory. Fix it by adding a loading.tsx file in your app directory.

      2. [React] You are missing a <Suspense> boundary in your component. Fix it by wrapping your component (or the entire app) in a <Suspense> component.

      3. [Next.js] The component is rendered in the root (outermost) layout.tsx or template.tsx file. Next.js does not wrap those files in a Suspense boundary, even if there is a loading.tsx file in the same folder. To fix it, wrap your layout inside a route group like this:

        - app
        - - layout.tsx  // contains <html> and <body>, alongside providers and other components that don't need ${options.caller ?? "this code path"}
        - - loading.tsx  // required for suspense
        - - (main)
        - - - layout.tsx  // contains the main layout of your app, like a sidebar or a header, and can use ${options.caller ?? "this code path"}
        - - - route.tsx  // your actual main page
        - - - the rest of your app

        For more information on this approach, see Next's documentation on route groups: https://nextjs.org/docs/app/building-your-application/routing/route-groups
      
      4. You caught this error with try-catch or a custom error boundary. Fix this by rethrowing the error or not catching it in the first place.

      See: https://nextjs.org/docs/messages/missing-suspense-with-csr-bailout

      More information on SSR and Suspense boundaries: https://react.dev/reference/react/Suspense#providing-a-fallback-for-server-errors-and-client-only-content
    `);
    this.name = "NoSuspenseBoundaryError";
    this.reason = options.caller ?? "suspendIfSsr()";
    this.digest = "BAILOUT_TO_CLIENT_SIDE_RENDERING";
  }
};
function suspendIfSsr(caller) {
  if (!isBrowserLike()) {
    throw new NoSuspenseBoundaryError({ caller });
  }
}
export {
  NoSuspenseBoundaryError,
  componentWrapper,
  forwardRefIfNeeded,
  getNodeText,
  mapRef,
  mapRefState,
  shouldRethrowRenderingError,
  suspend,
  suspendIfSsr,
  use,
  useQueryState,
  useRefState
};
//# sourceMappingURL=react.js.map